const API_BASE = process.env.REACT_APP_API_URL || '/api';

function getToken() {
  return localStorage.getItem('token');
}

export function setToken(token: string) {
  localStorage.setItem('token', token);
}

export function clearToken() {
  localStorage.removeItem('token');
}

export async function fetchJSON(path: string, options?: RequestInit) {
  const token = getToken();
  const headers: Record<string, string> = {
    ...(options?.headers as Record<string, string> || {}),
  };
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const res = await fetch(`${API_BASE}${path}`, { ...options, headers });
  if (res.status === 401) {
    clearToken();
    throw new Error('unauthorized');
  }
  if (!res.ok) {
    const body = await res.json().catch(() => null);
    throw new Error(body?.error || `${res.status} ${res.statusText}`);
  }
  // 빈 응답(예: write 후 data 없는 200) → null 로 안전 처리
  const text = await res.text();
  return text ? JSON.parse(text) : null;
}

export interface User {
  id: number;
  login_id: string;
  name: string;
  slack_user_id: string;
  role: string;
  profile_image?: string | null;
  github_username?: string | null;
  has_claude_token?: boolean;
  claude_token_set_at?: string | null;
  created_at?: string;
}

export interface Task {
  id: number;
  agent_name: string;
  type: string;
  title: string;
  status: string;
  branch: string | null;
  result: string | null;
  error: string | null;
  prompt: string | null;
  parent_task_id: number | null;
  agent_type: string | null;
  tmux_window: string | null;
  created_at: string;
  started_at: string | null;
  completed_at: string | null;
  requested_by: string | null;
  execution_log: string | null;
}

export interface Health {
  status: string;
  version: string;
  uptime: string;
  startedAt: string;
  hostname: string;
  platform: string;
  memory: { total: string; free: string; percent: string };
  cpu: string;
  gpu: string;
  node: string;
  db: string;
}

export interface AgentWindow {
  window: string;
  task: {
    id: number;
    agent_name: string;
    type: string;
    title: string;
    status: string;
    created_at: string;
    started_at: string | null;
  } | null;
  lastOutput: string;
}

export interface TaskGroup {
  taskId: number;
  title: string;
  requestedBy: string | null;
  startedAt: string | null;
  agents: AgentWindow[];
}

export interface AgentStatus {
  totalWindows: number;
  agentWindows: number;
  agents: AgentWindow[];
  taskGroups: TaskGroup[];
}

export interface TasksResponse {
  tasks: Task[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}
export const getTasks = (params?: string): Promise<TasksResponse> => fetchJSON(`/tasks${params ? '?' + params : ''}`);
export const getTask = (id: number) => fetchJSON(`/tasks/${id}`);
export const getTaskLog = (id: number) => fetchJSON(`/tasks/${id}/log`);
export const getHealth = () => fetchJSON('/health');
export const getAgentStatus = (): Promise<AgentStatus> => fetchJSON('/agents/status');
export const executeTask = (title: string, prompt: string, useTokenOf?: string) =>
  fetchJSON('/tasks/execute', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ title, prompt, ...(useTokenOf ? { use_token_of: useTokenOf } : {}) }),
  });

// Auth
export const login = (login_id: string, password: string) =>
  fetchJSON('/auth/login', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ login_id, password }),
  });

export const getInvite = (code: string) => fetchJSON(`/auth/invite/${code}`);

export const register = (code: string, login_id: string, name: string, password: string) =>
  fetchJSON('/auth/register', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ code, login_id, name, password }),
  });

// Plans
export interface Plan {
  id: number;
  task_id: number;
  agent_name: string;
  summary: string;
  detail: string | null;
  s3_key: string | null;
  status: string;
  created_at: string;
}

export interface PlanWithTask extends Plan {
  task_title: string;
  task_status: string;
  requested_by: string | null;
  requester_name: string | null;
}

export const getTaskPlans = (taskId: number): Promise<{ task: Task; plans: any[]; subTasks: Task[] }> =>
  fetchJSON(`/tasks/${taskId}/plans`);

export const getAllPlans = (): Promise<PlanWithTask[]> => fetchJSON('/tasks/plans/all');
export const getPlanMarkdown = (planId: number): Promise<{ id: number; s3_key: string; markdown: string }> =>
  fetchJSON(`/tasks/plans/${planId}/markdown`);

export const stopTask = (id: number) =>
  fetchJSON(`/tasks/${id}/stop`, { method: 'POST' });

export const getMe = (): Promise<User> => fetchJSON('/auth/me');
export const updateClaudeToken = (claude_token: string) =>
  fetchJSON('/auth/me/claude-token', {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ claude_token }),
  });
export const deleteClaudeToken = () =>
  fetchJSON('/auth/me/claude-token', { method: 'DELETE' });

// Telemetry
export interface TelemetrySummary {
  user_email: string;
  model: string;
  date: string;
  total_input: string;
  total_output: string;
  total_cache_read: string;
  total_cache_creation: string;
  total_cost: string;
  request_count: string;
}
export const getTelemetrySummary = (params?: { from?: string; to?: string; days?: number }): Promise<TelemetrySummary[]> => {
  const q: string[] = [];
  if (params?.from) q.push(`from=${params.from}`);
  if (params?.to) q.push(`to=${params.to}`);
  if (params?.days) q.push(`days=${params.days}`);
  if (q.length === 0) q.push(`from=${new Date().toISOString().slice(0, 10)}`);
  return fetch(`${API_BASE.replace('/api', '')}/v1/summary?${q.join('&')}`).then(r => r.json());
};

export interface TelemetryHourly {
  user_email: string;
  hour: string;
  input_tokens: string;
  output_tokens: string;
  cost_usd: string;
  request_count: string;
}

export interface TelemetryDaily {
  user_email: string;
  date: string;
  input_tokens: string;
  output_tokens: string;
  cost_usd: string;
  request_count: string;
}

export const getTelemetryHourly = (date?: string): Promise<TelemetryHourly[]> =>
  fetch(`${API_BASE.replace('/api', '')}/v1/hourly?date=${date || new Date().toISOString().slice(0, 10)}`).then(r => r.json());

export const getTelemetryDaily = (from?: string, to?: string): Promise<TelemetryDaily[]> => {
  const f = from || new Date(Date.now() - 30 * 86400000).toISOString().slice(0, 10);
  const t = to || new Date().toISOString().slice(0, 10);
  return fetch(`${API_BASE.replace('/api', '')}/v1/daily?from=${f}&to=${t}`).then(r => r.json());
};
export const updateMe = (data: { name?: string; profile_image?: string | null }) =>
  fetchJSON('/auth/me', {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
export const getUsers = () => fetchJSON('/auth/users');
export const updateUserRole = (id: number, role: string) =>
  fetchJSON(`/auth/users/${id}/role`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ role }),
  });
export const deactivateUser = (id: number, password: string) =>
  fetchJSON(`/auth/users/${id}/deactivate`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ password }),
  });
export const activateUser = (id: number) =>
  fetchJSON(`/auth/users/${id}/activate`, { method: 'POST' });
export const requestPasswordReset = (user_id: number) =>
  fetchJSON('/auth/password-reset', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ user_id }) });
export const confirmPasswordReset = (token: string, new_password: string) =>
  fetchJSON('/auth/password-reset/confirm', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ token, new_password }) });
export const updateGithubUsername = (id: number, github_username: string | null) =>
  fetchJSON(`/auth/users/${id}/github`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ github_username }),
  });

// DB Approvals
export interface DbApproval {
  id: number;
  type: string;
  title: string;
  detail: string | null;
  target_db: string | null;
  target_table: string | null;
  record_count: number | null;
  task_id: number | null;
  task_title: string | null;
  requested_by: string | null;
  requested_at: string;
  status: string;
  approved_by: string | null;
  approved_at: string | null;
  executed_at: string | null;
  execution_result: string | null;
}

export const getDbApprovals = (status?: string): Promise<DbApproval[]> =>
  fetchJSON(`/db-approvals${status && status !== 'all' ? `?status=${status}` : ''}`);

// S3 Docs (DB cached)
export interface S3Doc {
  id: number;
  s3_key: string;
  project: string;
  doc_type: string;
  filename: string;
  size: number;
  author: string | null;
  last_modified: string;
  synced_at: string;
}

export interface S3DocContent {
  key: string;
  markdown: string;
  author: string | null;
  lastModified: string;
}

export const getDocs = (project?: string, type?: string): Promise<S3Doc[]> => {
  const params: string[] = [];
  if (project) params.push(`project=${project}`);
  if (type) params.push(`type=${type}`);
  return fetchJSON(`/docs${params.length ? '?' + params.join('&') : ''}`);
};

export const getDocProjects = (): Promise<string[]> => fetchJSON('/docs/projects');
export const getDocTypes = (): Promise<string[]> => fetchJSON('/docs/types');

export const syncDocs = (): Promise<{ ok: boolean; synced: number }> =>
  fetchJSON('/docs/sync', { method: 'POST' });

export const getDocContent = (key: string): Promise<S3DocContent> =>
  fetchJSON(`/docs/content?key=${encodeURIComponent(key)}`);

// AI 측정 원본(압축본 등) — 5분짜리 S3 서명 URL
export const getDocDownloadUrl = (key: string): Promise<{ url: string }> =>
  fetchJSON(`/docs/download?key=${encodeURIComponent(key)}`);

export const deleteDoc = (key: string): Promise<{ ok: boolean; deleted: string }> =>
  fetchJSON(`/docs?key=${encodeURIComponent(key)}`, { method: 'DELETE' });

// Stores
export const getStores = (): Promise<{ id: number; name: string }[]> =>
  fetchJSON('/stores');

export interface StoreDetail {
  id: number;
  name: string;
  logo: string | null;
  ownerName: string | null;
  phone: string | null;
  isView: boolean;
}
export const getStoresDetail = (): Promise<StoreDetail[]> =>
  fetchJSON('/stores/detail');
export const updateStoreVisibility = (storeId: number, isView: boolean) =>
  fetchJSON('/stores/visibility', { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ storeId, isView }) });

// Coupon
export interface CouponType {
  id: number;
  couponName: string;
  validity: number;
  couponCategory: string;
  isActive: boolean;
}

export interface CouponHistory {
  id: number;
  couponName: string;
  couponTypeId: number | null;
  startDate: string;
  endDate: string;
  requestedBy?: string | null;
}

export interface BillingHistory {
  id: number;
  itemName: string;
  price: number;
  merchantUid: string;
  customerName: string;
  status: number;
  approvalDate: string;
}

export interface StoreCoach {
  teacherId: number;
  name: string;
  phone: string;
  profile: string | null;
  linkCoachId: string;
  isMemberAuth: boolean;
  isExerciseAuth: boolean;
  isSalesAuth: boolean;
}

export interface StoreBillingInfo {
  couponHistories: CouponHistory[];
  billingHistories: BillingHistory[];
  coaches: StoreCoach[];
  nextApprovalDate: string | null;
  billingStatus: number | null;          // 0:신청중, 1:진행중, 2:정지, null:구독 없음
  currentPlanId?: number | null;         // 현재 사용중 구독권 ID (없으면 null)
  currentPlanName?: string | null;       // 현재 구독권 이름 (플랜 삭제 시 null)
  currentItemName?: string | null;       // 결제 당시 품목명 스냅샷
  currentPrice?: number | null;          // 현재 결제 금액(원)
  currentValidityDays?: number | null;   // 현재 구독권 유효 기간(일)
}

export const getCouponTypes = (): Promise<CouponType[]> => fetchJSON('/coupon-types');
export const addCouponType = (data: { couponName: string; validity: number; couponCategory: string; isActive: boolean }) =>
  fetchJSON('/coupon-types', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) });
export const getStoreBilling = (storeId: number): Promise<StoreBillingInfo> => fetchJSON(`/store-billing?storeId=${storeId}`);
export const extendBillingDate = (storeId: number, extendDays: number) =>
  fetchJSON('/store-billing/extend', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ storeId, extendDays }) });
export const issueCoupon = (data: { storeId: number; couponTypeId: number; userId: string }) =>
  fetchJSON('/coupon-issue', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) });
export const deleteCoach = (storeId: number, teacherId: number) =>
  fetchJSON('/hub/coach/delete', { method: 'DELETE', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ storeId, teacherId }) });

// ===== Hub 부계정(서브어카운트) 관리 =====
export type SubAccountRole = 'AI' | 'MEMBER' | 'CLASS' | 'STATS' | 'LOCKER' | 'BOARD' | 'NOTE' | 'NOTICE' | 'SETTING';
export const SUB_ACCOUNT_ROLES: { code: SubAccountRole; label: string }[] = [
  { code: 'MEMBER', label: '회원' },
  { code: 'CLASS', label: '수업' },
  { code: 'STATS', label: '통계' },
  { code: 'LOCKER', label: '락커' },
  { code: 'BOARD', label: '보드' },
  { code: 'NOTE', label: '노트' },
  { code: 'NOTICE', label: '공지' },
  { code: 'SETTING', label: '설정' },
  { code: 'AI', label: 'AI' },
];

export interface SubAccount {
  id: number;
  account: string;
  name: string;
  phone: string;
  role: string;                 // 권한 코드 CSV (예: "MEMBER,STATS,NOTICE")
  isBilling: boolean | null;
  countryCode: string | null;
  countryIso: string | null;
  teacherId: number | null;     // 연동 코치 ID (nullable)
}

export interface PasswordResetResult {
  account: string;
  name?: string;
  phone: string;
  tempPassword: string;
  smsSuccess: boolean;
  smsReturnCode?: string | null;
  smsDescription?: string | null;
  resetAt: string;
}

export interface SecondPasswordResetResult {
  storeId: number;
  resetTo: string;
  alreadyDefault: boolean;
  resetAt: string;
}

export const getSubAccounts = (storeId: number): Promise<SubAccount[]> =>
  fetchJSON('/hub/store/sub-account/list', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ storeId }) });
export const createSubAccount = (data: { storeId: number; account: string; password: string; name: string; phone: string; countryCode?: string; countryIso?: string; role: string[]; isBilling?: boolean }) =>
  fetchJSON('/hub/store/sub-account/create', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) });
export const modifySubAccount = (data: { storeId: number; subAccountId: number; name?: string; phone?: string; countryCode?: string; countryIso?: string; role?: string[]; isBilling?: boolean }) =>
  fetchJSON('/hub/store/sub-account/modify', { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) });
export const deleteSubAccount = (storeId: number, subAccountId: number) =>
  fetchJSON('/hub/store/sub-account/delete', { method: 'DELETE', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ storeId, subAccountId }) });
export const resetStorePassword = (storeId: number): Promise<PasswordResetResult> =>
  fetchJSON('/hub/store/password/reset', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ storeId }) });
export const resetSubAccountPassword = (storeId: number, subAccountId: number): Promise<PasswordResetResult> =>
  fetchJSON('/hub/store/sub-account/password/reset', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ storeId, subAccountId }) });
export const resetStoreSecondPassword = (storeId: number): Promise<SecondPasswordResetResult> =>
  fetchJSON('/hub/store/second-password/reset', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ storeId }) });

// ===== Hub 구독권(subscription-plan) 관리 =====
export interface SubscriptionPlan {
  id: number;
  planName: string;
  price: number;
  validityDays: number;
  description: string | null;
  isActive: boolean;
  sortOrder: number;
}

export const getSubscriptionPlans = (): Promise<SubscriptionPlan[]> =>
  fetchJSON('/hub/subscription-plan/list');
export const saveSubscriptionPlan = (data: { planName: string; price: number; validityDays: number; description?: string; sortOrder?: number }) =>
  fetchJSON('/hub/subscription-plan/save', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) });
export const modifySubscriptionPlan = (data: { id: number; planName?: string; price?: number; validityDays?: number; description?: string; sortOrder?: number }) =>
  fetchJSON('/hub/subscription-plan/modify', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) });
export const toggleSubscriptionPlan = (planId: number) =>
  fetchJSON(`/hub/subscription-plan/toggle-active/${planId}`, { method: 'POST', headers: { 'Content-Type': 'application/json' } });
export const deleteSubscriptionPlan = (planId: number) =>
  fetchJSON(`/hub/subscription-plan/delete/${planId}`, { method: 'DELETE', headers: { 'Content-Type': 'application/json' } });
export const changeStorePlan = (storeId: number, planId: number) =>
  fetchJSON('/hub/subscription-plan/change-store-plan', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ storeId, planId }) });

// Message Pricing
export interface MessagePricing {
  id: number;
  messageType: string;
  unitPrice: number;
  description: string;
}
export const getMessagePricing = (): Promise<MessagePricing[]> => fetchJSON('/message-pricing');
export const updateMessagePricing = (data: { messageType: string; unitPrice: number; description?: string }): Promise<MessagePricing> =>
  fetchJSON('/message-pricing', { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) });

// Migration (Zone_Transform)
export interface MigrationJob {
  id: number;
  platform: string;
  center: string;
  store_id: number;
  store_name: string | null;
  status: string;
  phase: string;
  crawl_result: any | null;
  insert_dev_result: any | null;
  insert_live_result: any | null;
  requested_by: string | null;
  requested_at: string;
  completed_at: string | null;
  error: string | null;
  progress_stage: string | null;
  progress_current: number;
  progress_total: number;
  progress_updated_at: string | null;
}

export const getMigrationJobs = (status?: string): Promise<MigrationJob[]> =>
  fetchJSON(`/migration${status && status !== 'all' ? `?status=${status}` : ''}`);

export const createMigrationJob = (data: {
  platform: string;
  center: string;
  store_id: number;
  user_id: string;
  password: string;
  member_filter?: string;
  gym_id?: string;
}): Promise<MigrationJob> =>
  fetchJSON('/migration', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });

export const migrationInsertDev = (id: number, dryRun?: boolean): Promise<any> =>
  fetchJSON(`/migration/${id}/insert-dev`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ dry_run: dryRun }),
  });

export const migrationRequestLive = (id: number): Promise<any> =>
  fetchJSON(`/migration/${id}/request-live`, { method: 'POST' });

export const migrationInsertLive = (id: number): Promise<any> =>
  fetchJSON(`/migration/${id}/insert-live`, { method: 'POST' });

export const migrationApprove = (id: number): Promise<any> =>
  fetchJSON(`/migration/${id}/approve`, { method: 'POST' });

export const migrationReject = (id: number, reason?: string): Promise<any> =>
  fetchJSON(`/migration/${id}/reject`, {
    method: 'POST',
    body: JSON.stringify({ reason: reason || '' }),
  });

export const getMigrationJob = (id: number): Promise<MigrationJob> =>
  fetchJSON(`/migration/${id}`);

// Image Upload
export async function uploadImage(file: File, taskId?: string): Promise<{ s3Key: string; url: string; markdown: string }> {
  const token = getToken();
  const formData = new FormData();
  formData.append('file', file);
  if (taskId) formData.append('task_id', taskId);

  const headers: Record<string, string> = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const res = await fetch(`${API_BASE}/upload/image`, { method: 'POST', headers, body: formData });
  if (res.status === 401) { clearToken(); throw new Error('unauthorized'); }
  if (!res.ok) {
    const body = await res.json().catch(() => null);
    throw new Error(body?.error || `${res.status} ${res.statusText}`);
  }
  return res.json();
}

// KPI
export interface KpiSummary {
  assignee: string;
  avg_progress: string;
  review_issue_count: string;
  latest_kpi_score: string | null;
}

export interface KpiScore {
  id: number;
  assignee: string;
  period_start: string;
  period_end: string;
  notion_page_id: string | null;
  task_completion_score: string | null;
  code_quality_score: string | null;
  deadline_score: string | null;
  commit_quality_score: string | null;
  total_score: string | null;
  grade: string | null;
  notes: string | null;
  created_at: string;
  efficiency_score: string | null;
  efficiency_detail: string | null;
  ai_total_tokens: string | null;
  ai_total_cost: string | null;
  ai_sessions: number | null;
  ai_requests: number | null;
  ai_commits_per_10k_tokens: string | null;
  ai_cost_per_progress: string | null;
  ai_note: string | null;
  commit_count: number | null;
  lines_added: number | null;
  lines_deleted: number | null;
  doc_count: number | null;
  unplanned_commits: number | null;
  unplanned_lines_added: number | null;
  unplanned_detail: Record<string, number> | null;
  last_sync_at: string | null;
  productivity_score: string | null;
  consistency_score: string | null;
  project_engagement_score: string | null;
  commit_size_score: string | null;
  ai_bonus: string | null;
  score_detail: Record<string, string> | null;
  pr_cycle_score: string | null;
  pr_merge_count: number | null;
  pr_cycle_time_avg: string | null;
}

export interface KpiTask {
  id: number;
  notion_page_id: string | null;
  assignee: string;
  project: string;
  task_code: string | null;
  task_title: string;
  sub_task: string;
  category: string | null;
  progress: number;
  status: string;
  quality_score: number | null;
  quality_notes: string | null;
  due_date: string | null;
  started_at: string | null;
  completed_at: string | null;
  created_at: string;
  updated_at: string;
  estimated_days: number | null;
  actual_days: number | null;
  complexity: string | null;
}

export interface KpiReview {
  id: number;
  kpi_task_id: number;
  reviewer: string | null;
  category: string | null;
  severity: string | null;
  description: string | null;
  resolved: boolean;
  created_at: string;
  task_code: string | null;
  assignee: string;
  task_title: string;
  project: string;
}

export const toggleReviewResolved = (id: number, resolved: boolean) =>
  fetchJSON(`/kpi/reviews/${id}/resolved`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ resolved }) });

export const getKpiSummary = (): Promise<KpiSummary[]> => fetchJSON('/kpi/summary');
export const getKpiScores = (params?: { assignee?: string; period_start?: string; period_end?: string }): Promise<KpiScore[]> => {
  const q: string[] = [];
  if (params?.assignee) q.push(`assignee=${params.assignee}`);
  if (params?.period_start) q.push(`period_start=${params.period_start}`);
  if (params?.period_end) q.push(`period_end=${params.period_end}`);
  return fetchJSON(`/kpi/scores${q.length ? '?' + q.join('&') : ''}`);
};
export const getKpiTasks = (params?: { assignee?: string; task_code?: string; status?: string }): Promise<KpiTask[]> => {
  const q: string[] = [];
  if (params?.assignee) q.push(`assignee=${params.assignee}`);
  if (params?.task_code) q.push(`task_code=${params.task_code}`);
  if (params?.status) q.push(`status=${params.status}`);
  return fetchJSON(`/kpi/tasks${q.length ? '?' + q.join('&') : ''}`);
};
export const getKpiReviews = (params?: { assignee?: string; severity?: string; resolved?: string }): Promise<KpiReview[]> => {
  const q: string[] = [];
  if (params?.assignee) q.push(`assignee=${params.assignee}`);
  if (params?.severity) q.push(`severity=${params.severity}`);
  if (params?.resolved !== undefined) q.push(`resolved=${params.resolved}`);
  return fetchJSON(`/kpi/reviews${q.length ? '?' + q.join('&') : ''}`);
};

export const syncKpiGit = (): Promise<any> => fetchJSON('/kpi/sync', { method: 'POST' });
export const evaluateKpi = (): Promise<any> => fetchJSON('/kpi/evaluate', { method: 'POST' });

// 노션 동기화 제안 — 캘린더/업무일지/깃 대사에서 자동 반영하기엔 근거가 약한 건
export interface SyncProposal {
  id: number;
  kind: 'STATUS_DRIFT' | 'DATE_DRIFT' | 'MISSING_CARD' | 'ORPHAN' | 'UNMATCHED';
  notion_page_id: string | null;
  assignee: string | null;
  title: string;
  proposal: { before?: string; after?: string; fields?: Record<string, any> };
  evidence: string | null;
  status: string;
  created_at: string;
}
export const getSyncProposals = (): Promise<{ pending: number; proposals: SyncProposal[] }> =>
  fetchJSON('/kpi/sync-proposals');
export const approveSyncProposal = (id: number): Promise<any> =>
  fetchJSON(`/kpi/sync-proposals/${id}/approve`, { method: 'POST' });
export const rejectSyncProposal = (id: number): Promise<any> =>
  fetchJSON(`/kpi/sync-proposals/${id}/reject`, { method: 'POST' });
export const updateTaskComplexity = (id: number, complexity: string): Promise<any> =>
  fetchJSON(`/kpi/tasks/${id}/complexity`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ complexity }) });

// Push Notification Templates
export interface PushTemplate {
  id: number;
  templateCode: string;
  project: string;
  category: string;
  title: string;
  body: string;
  titleEn: string | null;
  bodyEn: string | null;
  availableVariables: string | null;
  location: string | null;
  locationParams: string | null;
  target: 'MEMBER' | 'COACH' | 'ALL' | null;
  tagCode: string | null;
  tagName: string | null;
  tagColor: string | null;
  kakaoTemplate: string | null;
  description: string | null;
  isActive: boolean;
  createdDate: string;
  modifiedDate: string;
}

export const getPushTemplates = (project?: string, category?: string): Promise<PushTemplate[]> => {
  const params: string[] = [];
  if (project) params.push(`project=${encodeURIComponent(project)}`);
  if (category) params.push(`category=${encodeURIComponent(category)}`);
  return fetchJSON(`/push-templates${params.length ? '?' + params.join('&') : ''}`);
};

export const updatePushTemplate = (data: {
  id: number;
  title: string;
  body: string;
  titleEn?: string | null;
  bodyEn?: string | null;
  isActive: boolean;
  location?: string | null;
  tagCode?: string | null;
  kakaoTemplate?: string | null;
  description?: string | null;
}): Promise<PushTemplate> =>
  fetchJSON('/push-templates', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });

// Kakao Alimtalk Templates
export interface KakaoTemplate {
  id: number;
  templateCode: string;
  kakaoTemplateCode: string;
  project: string;
  category: string;
  division: string;
  title: string;
  body: string;
  availableVariables: string | null;
  buttonName: string | null;
  buttonType: string | null;
  buttonSchemeAndroid: string | null;
  buttonSchemeIos: string | null;
  buttonUrlMobile: string | null;
  buttonUrlPc: string | null;
  isBillable: boolean;
  description: string | null;
  isActive: boolean;
  createdDate: string;
  modifiedDate: string;
}

export const getKakaoTemplates = (project?: string): Promise<KakaoTemplate[]> => {
  const qs = project ? `?project=${encodeURIComponent(project)}` : '';
  return fetchJSON(`/kakao-templates${qs}`);
};

// DAU/MAU
export interface ActiveUsers {
  zoneDau: number;
  zoneMau: number;
  coachDau: number;
  coachMau: number;
  date: string;
  monthStart: string;
  monthEnd: string;
}

export interface DailyCount {
  date: string;
  count: number;
}

export interface MonthlyCount {
  month: string;
  count: number;
}

export interface ActiveUsersTrend {
  zoneDauTrend: DailyCount[];
  coachDauTrend: DailyCount[];
  zoneMauTrend: MonthlyCount[];
  coachMauTrend: MonthlyCount[];
  startDate: string;
  endDate: string;
  mauMonths: number;
}

export const getActiveUsers = (date?: string): Promise<ActiveUsers> =>
  fetchJSON(`/hub/active-users${date ? `?date=${date}` : ''}`);

export const getActiveUsersTrend = (startDate?: string, endDate?: string, mauMonths?: number): Promise<ActiveUsersTrend> => {
  const q: string[] = [];
  if (startDate) q.push(`startDate=${startDate}`);
  if (endDate) q.push(`endDate=${endDate}`);
  if (mauMonths) q.push(`mauMonths=${mauMonths}`);
  return fetchJSON(`/hub/active-users/trend${q.length ? '?' + q.join('&') : ''}`);
};

// Messaging APIs
export interface MessagingMonthSummary {
  month: string;
  sms: { sms: number; lms: number; total: number };
  alimtalk: { sent: number; success: number; failed: number };
  costBreakdown: { sms: number; lms: number; alimtalk: number; total: number };
}

export interface SmsTrendItem {
  date: string;
  sms: number;
  lms: number;
  total: number;
  smsCost: number;
  lmsCost: number;
}

export interface AlimtalkTrendItem {
  date: string;
  sent: number;
  success: number;
  failed: number;
  cost: number;
}

export interface StoreRankingItem {
  storeId: number;
  storeName: string;
  sms: number;
  lms: number;
  alimtalk: number;
  totalCost: number;
}

export interface AlimtalkTemplateStatsItem {
  templateCode: string;
  sent: number;
  ratio: number;
}

export const getMessagingSummary = (startMonth: string, endMonth: string): Promise<{ months: MessagingMonthSummary[] }> =>
  fetchJSON(`/hub/messaging/summary?startMonth=${startMonth}&endMonth=${endMonth}`);

export const getSmsTrend = (startMonth: string, endMonth: string, interval?: string): Promise<SmsTrendItem[]> => {
  const q = `startMonth=${startMonth}&endMonth=${endMonth}${interval ? `&interval=${interval}` : ''}`;
  return fetchJSON(`/hub/messaging/sms/trend?${q}`);
};

export const getAlimtalkTrend = (startMonth: string, endMonth: string, interval?: string): Promise<AlimtalkTrendItem[]> => {
  const q = `startMonth=${startMonth}&endMonth=${endMonth}${interval ? `&interval=${interval}` : ''}`;
  return fetchJSON(`/hub/messaging/alimtalk/trend?${q}`);
};

export const getStoreRanking = (month?: string, limit?: number): Promise<{ ranking: StoreRankingItem[] }> => {
  const q: string[] = [];
  if (month) q.push(`month=${month}`);
  if (limit) q.push(`limit=${limit}`);
  return fetchJSON(`/hub/messaging/store-ranking${q.length ? '?' + q.join('&') : ''}`);
};

export const getAlimtalkTemplateStats = (month?: string): Promise<{ templates: AlimtalkTemplateStatsItem[] }> =>
  fetchJSON(`/hub/messaging/alimtalk/template-stats${month ? `?month=${month}` : ''}`);

// Hub 어드민 SMS / 인증번호 발송 + 발송 이력
export interface AdminSmsSendResult {
  totalCount: number;
  successCount: number;
  failedCount: number;
  smsType: 'SMS' | 'LMS';
  sender: string;
  results: Array<{
    countryCode: string;
    phone: string;
    success: boolean;
    returnCode: string;
    description: string;
  }>;
}

export interface AdminVerifiedCodeSendResult {
  verifiedCodeId: number;
  authNumber: string;
  expirationDate: string;
  countryCode?: string;  // 발급 국가코드 (백엔드 국제 대응 반영 후 내려옴)
  content?: string;      // 실제 발송된 문자 본문 (복사용 원문)
  smsResult: {
    smsType: 'SMS' | 'LMS';
    sender: string;
    success: boolean;
    returnCode: string;
    description: string;
  };
}

export interface AdminMessageSendItem {
  id: number;
  sendType: 'CUSTOM' | 'SIGNUP_ADMIN' | string;
  countryCode: string;
  phone: string;
  name: string | null;
  birthDay: string | null;  // 수신자 생년월일 YYYYMMDD (SIGNUP_ADMIN: admin_verified_code 조인)
  sender: string;
  title: string | null;
  content: string;
  smsType: 'SMS' | 'LMS';
  returnCode: string | null;
  description: string | null;
  success: boolean;
  sendUserId: string;
  createdDate: string;
}

export interface AdminSendHistoryResponse {
  items: AdminMessageSendItem[];
  total: number;
  page: number;
  size: number;
}

export const sendAdminSms = (body: {
  sendType: string;
  title?: string;
  content: string;
  recipients: Array<{ countryCode: string; phone: string; name?: string }>;
}): Promise<AdminSmsSendResult> =>
  fetchJSON('/hub/messaging/sms/admin-send', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });

export const sendAdminVerifiedCode = (body: {
  name: string;
  birth: string;
  countryCode: string;
  phone: string;
}): Promise<AdminVerifiedCodeSendResult> =>
  fetchJSON('/hub/messaging/admin-verified-code/send', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });

// 인증번호 발급 후 미사용(미가입) 회원 조회
export interface RegistrationFailedMember {
  name: string;
  countryCode: string;
  phone: string;
  birthDay: string;
  failCount: number;       // 기간 내 인증번호 발급 횟수
  latestSendDate: string;  // 가장 최근 인증번호 발급일
  expired: boolean;        // 최근 발급 인증번호 만료 여부 (true=재발급 필요)
}

export interface RegistrationFailedMembersResponse {
  totalSent: number;
  distinctPhones: number;
  registeredCount: number;
  failedCount: number;
  members: RegistrationFailedMember[];
}

export const getRegistrationFailedMembers = (params: { from?: string; to?: string } = {}): Promise<RegistrationFailedMembersResponse> => {
  const q: string[] = [];
  if (params.from) q.push(`from=${params.from}`);
  if (params.to) q.push(`to=${params.to}`);
  return fetchJSON(`/hub/messaging/registration/failed-members${q.length ? '?' + q.join('&') : ''}`);
};

export const getAdminSendHistory = (params: {
  sendType?: 'CUSTOM' | 'SIGNUP_ADMIN';
  page?: number;
  size?: number;
  from?: string;
  to?: string;
}): Promise<AdminSendHistoryResponse> => {
  const q: string[] = [];
  if (params.sendType) q.push(`sendType=${params.sendType}`);
  if (params.page) q.push(`page=${params.page}`);
  if (params.size) q.push(`size=${params.size}`);
  if (params.from) q.push(`from=${params.from}`);
  if (params.to) q.push(`to=${params.to}`);
  return fetchJSON(`/hub/messaging/admin-send/history${q.length ? '?' + q.join('&') : ''}`);
};

// Store Status & Billing Summary
export interface StoreStatusDTO {
  storeId: number;
  storeName: string;
  storeLogo: string | null;
  ownerName: string | null;
  phone: string | null;
  isView: boolean;
  status: 'BILLING' | 'COUPON' | 'EXPIRING' | 'NONE';
  activeCouponName: string | null;
  couponEndDate: string | null;
  subscribeStatus: number;        // 1:진행중, 2:해지예약(결제일까지 유지), -1:없음 (DB status 패스스루)
  nextApprovalDate?: string | null; // 해지예약 시 유지 종료일(다음 결제일)
}

export interface StoreStatusSummary {
  stores: StoreStatusDTO[];
  total: number;
  billingCount: number;
  couponCount: number;
  expiringCount: number;
  noneCount: number;
}

export interface MonthlyBillingDTO {
  month: string;
  subscribe: number;
  notification: number;
  total: number;
}

export interface BillingSummaryDTO {
  currentMonthBilled: number;
  currentMonthPending: number;
  currentMonthSubscribe: number;
  currentMonthNotification: number;
  prevMonthBilled: number;
  prevMonthSubscribe: number;
  prevMonthNotification: number;
  monthlyTrend: MonthlyBillingDTO[];
  activeStoreCount: number;
  totalStoreCount: number;
}

export interface CouponExpiringDTO {
  storeId: number;
  storeName: string;
  couponName: string;
  endDate: string;
  daysLeft: number;
  hasBilling: boolean;
}

export interface BillingTrendItem {
  date: string;
  subscribe: number;
  notification: number;
  total: number;
  subscribeCount: number;
  notificationCount: number;
}

export interface MonthlySignup {
  month: string;
  signupCount: number;
  cardRegistered: number;
  couponUsed: number;
  paidConverted: number;
  conversionRate: number;
}

export interface SignupStatsDTO {
  monthlySignups: MonthlySignup[];
  currentMonth: { signupCount: number; paidConverted: number; conversionRate: number };
  prevMonth: { signupCount: number; paidConverted: number; conversionRate: number };
  funnelTotal: { signup: number; cardRegistered: number; couponUsed: number; paidConverted: number };
}

export const getSignupStats = (months?: number): Promise<SignupStatsDTO> =>
  fetchJSON(`/hub/store/signup-stats${months ? `?months=${months}` : ''}`);

export const getStoreStatusSummary = (): Promise<StoreStatusSummary> =>
  fetchJSON('/hub/store/status-summary');

export const getBillingSummary = (months?: number): Promise<BillingSummaryDTO> =>
  fetchJSON(`/hub/store/billing-summary${months ? `?months=${months}` : ''}`);

export const getCouponExpiring = (days?: number): Promise<CouponExpiringDTO[]> =>
  fetchJSON(`/hub/store/coupon-expiring${days ? `?days=${days}` : ''}`);

export const getBillingTrend = (date: string): Promise<BillingTrendItem[]> =>
  fetchJSON(`/hub/store/billing-trend?date=${date}`);

// Passkey (WebAuthn)
export const getPasskeyRegisterOptions = () => fetchJSON('/auth/passkey/register/options', { method: 'POST' });
export const verifyPasskeyRegister = (credential: any, deviceName?: string) =>
  fetchJSON('/auth/passkey/register/verify', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ credential, deviceName }) });
export const getPasskeyAuthOptions = (tempToken: string) =>
  fetch(`${API_BASE}/auth/passkey/auth/options`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ tempToken }) }).then(r => r.json());
export const verifyPasskeyAuth = (tempToken: string, credential: any) =>
  fetch(`${API_BASE}/auth/passkey/auth/verify`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ tempToken, credential }) }).then(r => r.json());
export const getPasskeys = (): Promise<{ id: number; device_name: string; created_at: string }[]> => fetchJSON('/auth/passkeys');
export const deletePasskey = (id: number) => fetchJSON(`/auth/passkey/${id}`, { method: 'DELETE' });

// Members (Coach Server)
export interface HubMember {
  userId: string;
  name: string;
  phone: string;
  profile: string | null;
  signUpType: string;
  createdDate: string;
}
export interface HubMemberPage {
  totalCount: number;
  page: number;
  members: HubMember[];
}
export interface HubMemberDetail {
  userId: string;
  name: string;
  phone: string;
  profile: string | null;
  signUpType: string;
  gender: string | null;
  age: number | null;
  createdDate: string;
  lastAccessDate: string | null;
  isAvailableNotification: boolean;
  countryCode: string | null;
  countryIso: string | null;
  os: string | null;
  appVersion: string | null;
  buildVersion: number | null;
  deviceModel: string | null;
  isCardRegistered: boolean;
  registeredStores: { storeId: number; storeName: string; storeLogo: string | null; ownerName: string; storeTel: string }[];
  eventParticipations: { eventId: number; eventName: string; eventLogo: string | null }[];
}
export const getHubMembers = (page: number, limit: number, search?: string): Promise<HubMemberPage> => {
  const params = `page=${page}&limit=${limit}${search ? `&search=${encodeURIComponent(search)}` : ''}`;
  return fetchJSON(`/hub/members?${params}`);
};
export const getHubMemberDetail = (targetUserId: string): Promise<HubMemberDetail> =>
  fetchJSON(`/hub/member/${encodeURIComponent(targetUserId)}`);

// Withdrawal requests
export interface WithdrawalRequest {
  userId: string;
  name: string;
  phone: string;
  profile: string | null;
  signUpType: string;
  createdDate: string;
  withdrawalRequestDate: string;
  withdrawalScheduledDate: string;
}
export interface WithdrawalPage {
  totalCount: number;
  page: number;
  members: WithdrawalRequest[];
}
export const getWithdrawalRequests = (page = 1, limit = 20, search?: string): Promise<WithdrawalPage> => {
  const params = `page=${page}&limit=${limit}${search ? `&search=${encodeURIComponent(search)}` : ''}`;
  return fetchJSON(`/hub/withdrawal-requests?${params}`);
};

// 지급대행(링코페이 정산) 셀러 — Link_Dev_Back zone 프록시
export interface PayoutSellerListItem {
  storeId: number;
  storeName: string;
  businessNumber: string;
  representative: string;
  status: string;       // NOT_APPLIED | APPLIED | PENDING | APPROVED | REJECTED
  statusLabel: string;  // 화면 표시용 라벨 (백엔드 제공)
  rejectReason: string | null; // status=REJECTED 일 때만 값. Hub 응답에만 실리고 업체에는 안 나간다
  settlementCycle: string;
  registeredAt: string;
}
export interface PayoutSellerListResponse {
  totalCount: number;
  page: number;   // 0-base
  size: number;
  items: PayoutSellerListItem[];
}
export interface PayoutSettlement {
  type: string;            // SUCCESS | FAILED
  settledDate: string;
  startAt: string;
  endAt: string;
  settlementAmount: number;
  status: string;          // REQUESTED | IN_PROGRESS | COMPLETED | FAILED | CANCELED
  statusLabel: string;
  errorCode: string | null;
}
export interface PayoutSellerDetail {
  storeId: number;
  storeName: string;
  businessNumber: string;
  businessType: string;
  businessLicenseUrl: string | null;
  representative: string;
  status: string;
  statusLabel: string;
  rejectReason: string | null; // status=REJECTED 일 때만 값
  sellerStatus: string;    // Toss 원본 (참고용)
  account: { bankName: string; accountNumber: string; holderName: string };
  email: string;
  phone: string;
  settlementCycle: string;
  registeredAt: string;
  settlements: PayoutSettlement[];
}
export const getPayoutSellers = (status?: string, keyword?: string, page = 0, size = 20): Promise<PayoutSellerListResponse> => {
  const params = new URLSearchParams({ page: String(page), size: String(size) });
  if (status) params.append('status', status);
  if (keyword) params.append('keyword', keyword);
  return fetchJSON(`/hub/payout/sellers?${params.toString()}`);
};
export const getPayoutSellerDetail = (storeId: number): Promise<PayoutSellerDetail> =>
  fetchJSON(`/hub/payout/seller/${storeId}`);
export interface PayoutSettlementBucket {
  period: string;          // YYYY-MM
  amount: number;          // 해당 월 정산 시도 총액 (전체 status)
  count: number;           // 해당 월 정산 건수
  completedAmount: number; // 해당 월 지급완료(COMPLETED) 금액
  failedCount: number;     // 해당 월 정산 실패 건수
}
export interface PayoutSettlementSummary {
  groupBy: string;         // total | month
  totalAmount: number;     // 전체 COMPLETED 정산 총액
  totalCount: number;      // 전체 COMPLETED 정산 건수
  buckets: PayoutSettlementBucket[] | null; // groupBy=month 일 때만 (period DESC), total 이면 null
}
export const getPayoutSettlementSummary = (storeId: number, groupBy: 'total' | 'month' = 'total', from?: string, to?: string): Promise<PayoutSettlementSummary> => {
  const params = new URLSearchParams({ groupBy });
  if (from) params.append('from', from);
  if (to) params.append('to', to);
  return fetchJSON(`/hub/payout/seller/${storeId}/settlement-summary?${params.toString()}`);
};
// 심사 반려 — 사유는 Hub 에서만 조회되고 업체 마이페이지에는 '신청 심사중' 으로 보인다
export const rejectPayoutSeller = (storeId: number, reason: string): Promise<{ ok: boolean }> =>
  fetchJSON(`/hub/payout/seller/${storeId}/reject`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ reason }),
  });
// 심사 재승인 = 반려 해제 (심사중으로 되돌림)
export const reapprovePayoutSeller = (storeId: number): Promise<{ ok: boolean }> =>
  fetchJSON(`/hub/payout/seller/${storeId}/reapprove`, { method: 'POST' });

// Store members (Zone)
export interface StoreMember {
  id: number;
  linkCoachId: string;
  name: string;
  phone: string;
  profile: string;
  birthDay: string;
  gender: string;
}
export const getStoreMemberList = (storeId: number): Promise<StoreMember[]> =>
  fetchJSON(`/hub/store/member/list?storeId=${storeId}`);

// 국가코드 목록 (coach DB sms_country — 앱/웹과 동일한 원본을 그대로 사용)
export interface SMSCountry {
  countryNameEN: string;
  countryNameKR: string;
  countryCode: string;   // '+82', '+1-340' 처럼 지역번호가 붙은 값도 존재 — 원문 그대로 전송한다
  countryIso: string;    // sms_country.payment_code (KR, US ...)
  isAvailable: boolean;  // SMS 발송 가능 여부 (국가 유효성과는 무관)
  flagUrl: string;
}
export const getSMSCountryList = (): Promise<SMSCountry[]> =>
  fetchJSON('/hub/sms-country');

// Member actions
export const createMemberAccount = (data: { userId: string; password: string; name: string; nickName: string; phone?: string; birthDate?: string; gender?: string; isForeigner?: boolean; countryCode?: string; countryIso?: string; memberId?: number }) =>
  fetchJSON('/hub/member/create-account', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) });
export const requestWithdrawal = (userId: string, reason?: string) =>
  fetchJSON(`/hub/member/${encodeURIComponent(userId)}/withdrawal`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ reason }) });
export const cancelWithdrawal = (userId: string) =>
  fetchJSON(`/hub/member/${encodeURIComponent(userId)}/cancel-withdrawal`, { method: 'PATCH' });
export const processWithdrawal = (userId: string) =>
  fetchJSON(`/hub/member/${encodeURIComponent(userId)}/process-withdrawal`, { method: 'POST' });

// Subscription management
export const terminateSubscription = (storeId: number) =>
  fetchJSON('/hub/store/subscription/terminate', { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ storeId }) });
export const reactivateSubscription = (storeId: number) =>
  fetchJSON('/hub/store/subscription/reactivate', { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ storeId }) });
export const deleteStore = (storeId: number) =>
  fetchJSON('/hub/store/delete', { method: 'DELETE', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ storeId }) });

// User daily stats (signup / withdrawal)
export interface UserDailyStat {
  date: string;
  signupCount: number;
  withdrawRequestCount: number;
  withdrawProcessedCount: number;
}
export interface UserDailyStatsResponse {
  totalSignupCount: number;
  totalWithdrawCount: number;
  dailyStats: UserDailyStat[];
}
export const getUserDailyStats = (date: string): Promise<UserDailyStatsResponse> =>
  fetchJSON(`/hub/user/daily-stats?date=${date}`);

// Infrastructure monitoring
export interface InfraService {
  name: string;
  state: string;
  rps: number;
  requestCount: number;
  latencyMs: number | null;
  err5xx: number;
  err4xx: number;
  cpuPercent: number | null;
  instances: number;
}
export interface InfraDatabase {
  name: string;
  status: string;
  class: string;
  cpuPercent: number | null;
  connections: number | null;
  freeMemoryMB: number | null;
  freeStorageGB: number | null;
  swapUsageMB: number | null;
}
export interface InfraEcs {
  cluster: string;
  service: string;
  ec2Type: string | null;
  ec2Id: string | null;
  cpuPercent: number | null;
  memPercent: number | null;
}
export interface InfraOverview {
  summary: { totalRps: number; total5xx: number; totalDbConnections: number; ec2Running: number; rdsCount: number; avgLatencyMs: number | null; maxCpuPercent: number | null };
  services: InfraService[];
  databases: InfraDatabase[];
  ecs: InfraEcs[];
  local: any;
  timestamp: string;
}
export interface InfraTraffic {
  traffic: Record<string, { time: string; requests: number }[]>;
  latency: Record<string, { time: string; value: number }[]>;
  errors: Record<string, { time: string; err5xx: number; err4xx: number }[]>;
  serviceResources: Record<string, { cpu: { time: string; value: number }[]; mem: { time: string; value: number }[] }>;
  dbResources: Record<string, { cpu: { time: string; value: number }[]; conn: { time: string; value: number }[] }>;
  timestamp: string;
}
export const getInfraOverview = (): Promise<InfraOverview> => fetchJSON('/infra/overview');
export const getInfraTraffic = (): Promise<InfraTraffic> => fetchJSON('/infra/traffic');

export interface DevEc2Status {
  state: 'running' | 'pending' | 'stopping' | 'stopped' | 'shutting-down' | 'terminated' | string;
  ip: string | null;
  lastAction: { user: string; action: 'up' | 'down' | 'restart'; at: string } | null;
}
export const getDevEc2Status = (): Promise<DevEc2Status> => fetchJSON('/infra/dev-ec2');
export const controlDevEc2 = (action: 'up' | 'down'): Promise<{ ok: true; message: string }> =>
  fetchJSON(`/infra/dev-ec2/${action}`, { method: 'POST' });

export interface DevRdsStatus {
  state: 'available' | 'stopped' | 'starting' | 'stopping' | 'rebooting' | string;
  endpoint: string | null;
  lastAction: { user: string; action: 'up' | 'down'; at: string } | null;
}
export const getDevRdsStatus = (): Promise<DevRdsStatus> => fetchJSON('/infra/dev-rds');
export const controlDevRds = (action: 'up' | 'down'): Promise<{ ok: true; message: string }> =>
  fetchJSON(`/infra/dev-rds/${action}`, { method: 'POST' });

export interface DevService {
  name: 'zone' | 'coach' | 'exercise';
  deployed: boolean;
  running: boolean;
  state: string;
  image: string | null;
  uptime: string | null;
}
export interface DevServicesStatus {
  ec2: { state: string; ip: string | null };
  services: DevService[];
}
export const getDevServices = (): Promise<DevServicesStatus> => fetchJSON('/infra/dev-services');
export const controlDevService = (name: string, action: 'up' | 'down' | 'restart'): Promise<{ ok: true; stdout?: string; error?: string }> =>
  fetchJSON(`/infra/dev-services/${name}/${action}`, { method: 'POST' });
// EC2 인스턴스 강제 재부팅(stop --force → start). 즉시 반환되고 완료까지 약 1분 걸린다.
export const restartAllDevServices = (): Promise<{ ok: true; message?: string; error?: string }> =>
  fetchJSON('/infra/dev-services/restart-all', { method: 'POST' });

// 자체 호스팅 LLM (Ollama) — 개발 중 스모크 테스트용. 켜고/끄기 + 상태.
export interface LlmStatus {
  running: boolean;
  model: string;
  loadedModels: { name: string; sizeGB: number; expiresAt: string }[];
  memory: { availableGB: number; compressedGB: number } | null;
}
export const getLlmStatus = (): Promise<LlmStatus> => fetchJSON('/llm/status');
export const startLlm = (): Promise<{ running: boolean; started?: boolean; alreadyRunning?: boolean }> =>
  fetchJSON('/llm/start', { method: 'POST' });
export const stopLlm = (): Promise<{ running: boolean; stopped: boolean }> =>
  fetchJSON('/llm/stop', { method: 'POST' });

// Public document sharing (no auth required)
export const getSharedDoc = (token: string) =>
  fetch(`${API_BASE}/docs/shared/${token}`).then(res => {
    if (!res.ok) throw new Error('expired');
    return res.json();
  });

// ===== 발신번호 신청 (Sender Number) =====
export interface SenderNumberSummary {
  id: number;
  storeId: number;
  phoneNumber: string;
  alias: string;
  approvalStatus: 'APPLIED' | 'REVIEWING' | 'APPROVED' | 'REJECTED';
  rejectionReason: string | null;
  expiredDate: string | null;
  createdDate: string;
  modifiedDate: string;
  deleted: boolean;
}

export interface SenderNumberDetail extends SenderNumberSummary {
  countryCode: string;
  businessNumber: string;
  agreedTermsAt: string | null;
  businessRegistrationUrl: string | null;
  businessRegistrationFileName: string | null;
  telecomCertificateUrl: string | null;
  telecomCertificateFileName: string | null;
  applicantType: 'INDIVIDUAL' | 'CORPORATION' | null;
  address: string | null;
  idCardUrl: string | null;
  idCardFileName: string | null;
  sealCertificateUrl: string | null;
  sealCertificateFileName: string | null;
  employmentCertificateUrl: string | null;
  employmentCertificateFileName: string | null;
  stampUrl: string | null;
  stampOriginalUrl: string | null;
  delegationUrl: string | null;
  rejectedFields: string[];
}

// 첨부서류 다운로드 type (agent-server /hub/sender-number/:id/document?type=)
export type SenderDocType = 'business' | 'telecom' | 'idCard' | 'seal' | 'employment' | 'stamp' | 'stampOriginal' | 'delegation';

export interface SenderNumberPage {
  content: SenderNumberSummary[];
  totalCount: number;
  page: number;
  size: number;
  totalPages: number;
}

export const getSenderNumberApplications = (params: { status?: string; storeId?: number; keyword?: string; page?: number; size?: number }): Promise<SenderNumberPage> => {
  const q: string[] = [];
  if (params.status) q.push(`status=${encodeURIComponent(params.status)}`);
  if (params.storeId != null) q.push(`storeId=${params.storeId}`);
  if (params.keyword) q.push(`keyword=${encodeURIComponent(params.keyword)}`);
  if (params.page != null) q.push(`page=${params.page}`);
  if (params.size != null) q.push(`size=${params.size}`);
  return fetchJSON(`/hub/sender-number${q.length ? '?' + q.join('&') : ''}`);
};

export const getSenderNumberApplication = (id: number): Promise<SenderNumberDetail> =>
  fetchJSON(`/hub/sender-number/${id}`);

export const updateSenderNumberStatus = (id: number, body: { status: string; rejectionReason?: string; rejectedFields?: string[] }): Promise<SenderNumberDetail> =>
  fetchJSON(`/hub/sender-number/${id}/status`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });

export async function downloadSenderNumberDocument(id: number, type: SenderDocType, fileName: string): Promise<void> {
  const token = getToken();
  const headers: Record<string, string> = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const res = await fetch(`${API_BASE}/hub/sender-number/${id}/document?type=${type}`, { headers });
  if (res.status === 401) { clearToken(); throw new Error('unauthorized'); }
  if (!res.ok) {
    const body = await res.json().catch(() => null);
    throw new Error(body?.error || `${res.status} ${res.statusText}`);
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName || `sender-number-${id}-${type}`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ===== 이벤트 관리 (Link_Event_Back /api2/hub 프록시) =====
export interface EventOrganizer {
  organizerId: number;
  title: string;
  userId: string;
  startDate: string | null;
  endDate: string | null;
  applicationStartDate: string | null;
  applicationEndDate: string | null;
  isDisplay: boolean;
  isSubmit: boolean;
  isSeminar: boolean;
  viewOrder: number;
  createdDate: string | null;
  eventCount: number;
  totalParticipantCount: number;
  totalSalesAmount: number;
}
export const getEventList = (): Promise<EventOrganizer[]> => fetchJSON('/event/list');
export const createEventer = (data: { loginId: string; password: string; name: string; description?: string; logo?: string }) =>
  fetchJSON('/event/eventer', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) });
export const updateTournamentSubmit = (organizerId: number, isSubmit: boolean) =>
  fetchJSON('/event/tournament/submit', { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ organizerId, isSubmit }) });
export const updateTournamentDisplay = (organizerId: number, isDisplay: boolean) =>
  fetchJSON('/event/tournament/display', { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ organizerId, isDisplay }) });
export const updateTournamentOrder = (organizerId: number, viewOrder: number) =>
  fetchJSON('/event/tournament/order', { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ organizerId, viewOrder }) });

// 승인 자동검수
export interface InspectionCheck {
  key: string;
  label: string;
  grade?: 'critical' | 'consistency' | 'warning' | string;
  pass: boolean;
  current?: string;
  reference?: string;
  comment?: string;
}
export interface EventInspection {
  id: number;
  organizer_id: number;
  task_id: number | null;
  title: string | null;
  status: 'inspecting' | 'inspected' | 'approved' | 'rejected' | 'failed';
  ready: boolean | null;
  score: number | null;
  verdict: InspectionCheck[] | null;
  report_md: string | null;
  suggested_url_key: string | null;
  suggested_event_text: string | null;      // 표출 종목 텍스트 함축 제안 (한)
  suggested_event_text_en: string | null;   // 표출 종목 텍스트 함축 제안 (영)
  task_status: string | null;
  created_at: string;
}
// 자세히 보기 — 단일 대회 상세(편집용 전체 필드 + 통계 + 읽기전용 종목/약관)
export interface EventTicketPriceOption {
  id: number;
  eventId: number;
  appliedDate: string | null;
  name: string;
  price: number;
  priceUsd: number;
}
export interface EventTicket {
  eventId: number;
  organizerId: number;
  name: string;
  participationFee: number | null;
  isTeamFight: boolean | null;
  entryQualification: string | null;
  teamMemberCount: number;
  isUsePriceOption: boolean | null;
  place: string | null;
  ticketThumbnail: string | null;
  priceOptions: EventTicketPriceOption[];
}
export interface EventTerms {
  termsId: number;
  termsTitle: string | null;
  termsTitleEn: string | null;
  termsText: string | null;
  termsTextEn: string | null;
  isEssential: boolean | null;
}
export interface EventCompleteInfo {
  eventText: string | null;
  eventProfile: string | null;
}
// 기념품 설정 (souvenir_info) — 사이즈 축은 isUseOption 에 따라 sizes 또는 options 하위에 실림
export interface EventSouvenirOption {
  souvenirOptionId: number;
  name: string | null;
  sizes: string[] | null;
}
export interface EventSouvenir {
  souvenirId: number;
  name: string | null;
  division: string | null;   // Common | Additional
  souvenirPicture: string | null;
  price: number;
  priceUsd: number | null;
  isUseOption: boolean | null;
  sizes: string[] | null;
  options: EventSouvenirOption[] | null;
}
export interface EventDetail {
  organizerId: number;
  title: string;
  isSubmit?: boolean;
  // 편집 대상 — 기본정보
  mainPicture: string;
  bannerPicture: string;
  souvenirPicture: string;
  content: string;
  period: string;
  place: string;
  subjectivity: string;
  sponsor: string;
  support: string;
  columnText: string;
  eventText: string;
  eventTextEn: string;
  urlKey?: string;
  // 편집 대상 — 날짜 7종
  startDate: string | null;
  endDate: string | null;
  applicationStartDate: string | null;
  applicationEndDate: string | null;
  availableModifyDate: string | null;
  availableDeleteDate: string | null;
  myEventOpenDate: string | null;
  // 읽기전용
  tickets: EventTicket[];
  terms: EventTerms[];
  completeInfo: EventCompleteInfo | null;
  souvenirs?: EventSouvenir[];
  // 통계
  eventCount: number;
  totalParticipantCount: number;
  totalSalesAmount: number;
}
export const getTournamentDetail = (organizerId: number): Promise<EventDetail> =>
  fetchJSON(`/event/tournament/${organizerId}`);

// 기본정보 저장 (날짜 미변경) — Hub 전용 save
export interface TournamentSavePayload {
  organizerId: number;
  title: string;
  mainPicture: string;
  bannerPicture: string;
  souvenirPicture: string;
  content: string;
  period: string;
  place: string;
  subjectivity: string;
  sponsor: string;
  support: string;
  columnText: string;
  eventText: string;
  eventTextEn: string;
}
export const saveTournament = (data: TournamentSavePayload): Promise<EventDetail> =>
  fetchJSON('/event/tournament/save', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) });

// 날짜 7종 저장 (NOT NULL + 시작≤종료 검증)
export interface TournamentDatePayload {
  organizerId: number;
  startDate: string;
  endDate: string;
  applicationStartDate: string;
  applicationEndDate: string;
  availableModifyDate: string;
  availableDeleteDate: string;
  myEventOpenDate: string;
}
export const saveTournamentDate = (data: TournamentDatePayload) =>
  fetchJSON('/event/tournament/date', { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) });

export const inspectTournament = (organizerId: number): Promise<{ taskId: number; inspectionId: number; status: string }> =>
  fetchJSON(`/event/tournament/${organizerId}/inspect`, { method: 'POST' });
export const getLatestInspection = (organizerId: number): Promise<EventInspection | null> =>
  fetchJSON(`/event/inspections/${organizerId}/latest`);
export const approveTournament = (organizerId: number, urlKey: string, inspectionId?: number) =>
  fetchJSON(`/event/tournament/${organizerId}/approve`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ urlKey, inspectionId }) });

// ===== AI 로그 (에이전트 챗봇 / 문자문구 추천) =====
export type AiLogCategory = 'agent' | 'sms';

export interface AiLogCategoryStat {
  callCount: number;
  conversationCount: number;
  inputTokens: number;
  outputTokens: number;
  totalTokens: number;
  errorCount: number;
  avgElapsedMs: number;
}
export interface AiLogDailyTrend {
  date: string;
  agentCalls: number;
  smsCalls: number;
  agentTokens: number;
  smsTokens: number;
}
export interface AiLogSummary {
  range: { startDate: string; endDate: string };
  categories: { agent: AiLogCategoryStat; sms: AiLogCategoryStat };
  dailyTrend: AiLogDailyTrend[];
}
export interface AiLogItem {
  id: number;
  createdDate: string;
  category: AiLogCategory;
  callType: string;
  modelUsed: string | null;
  storeId: number | null;
  userId: string | null;
  requestMessage: string | null;
  intention: string | null;
  inputTokens: number | null;
  outputTokens: number | null;
  totalTokens: number | null;
  elapsedMs: number | null;
  error: boolean;
  generatedContent: string | null;
  conversationId: string | null;
}
export interface AiLogList {
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
  items: AiLogItem[];
}

export const getAiLogSummary = (startDate: string, endDate: string): Promise<AiLogSummary> =>
  fetchJSON(`/ai-logs/summary?startDate=${startDate}&endDate=${endDate}`);

export const getAiLogList = (params: {
  category?: 'agent' | 'sms' | 'all';
  startDate: string;
  endDate: string;
  storeId?: number;
  errorOnly?: boolean;
  page?: number;
  size?: number;
}): Promise<AiLogList> => {
  const q = new URLSearchParams();
  q.set('startDate', params.startDate);
  q.set('endDate', params.endDate);
  if (params.category) q.set('category', params.category);
  if (params.storeId !== undefined) q.set('storeId', String(params.storeId));
  if (params.errorOnly) q.set('errorOnly', 'true');
  if (params.page !== undefined) q.set('page', String(params.page));
  if (params.size !== undefined) q.set('size', String(params.size));
  return fetchJSON(`/ai-logs/list?${q.toString()}`);
};

// ── Fleet 로그 검색 ──────────────────────────────────────
export interface FleetLogEvent {
  ts: number;
  time: string;
  level: string;
  thread: string;
  className: string;
  message: string;
  logStream: string;
  raw: string;
}

export interface FleetLogSearchResult {
  total: number;
  results: FleetLogEvent[];
  histogram: { t: number; count: number }[];
  scannedFiles: number;
  truncated: boolean;
  capped?: boolean;
  note?: string;
}

export const getFleetList = (): Promise<{ fleets: string[] }> =>
  fetchJSON('/fleet-logs/fleets');

export const searchFleetLogs = (params: {
  fleet: string;
  start: number;
  end: number;
  q?: string;
  regex?: boolean;
  levels?: string[];
  className?: string;
  thread?: string;
  sort?: 'asc' | 'desc';
  limit?: number;
  offset?: number;
}): Promise<FleetLogSearchResult> => {
  const p = new URLSearchParams();
  p.set('fleet', params.fleet);
  p.set('start', String(params.start));
  p.set('end', String(params.end));
  if (params.q) p.set('q', params.q);
  if (params.regex) p.set('regex', '1');
  if (params.levels && params.levels.length) p.set('levels', params.levels.join(','));
  if (params.className) p.set('class', params.className);
  if (params.thread) p.set('thread', params.thread);
  if (params.sort) p.set('sort', params.sort);
  if (params.limit !== undefined) p.set('limit', String(params.limit));
  if (params.offset !== undefined) p.set('offset', String(params.offset));
  return fetchJSON(`/fleet-logs/search?${p.toString()}`);
};
